import React, { useCallback, useEffect, useState } from 'react';
import {
  Divider,
  Tag,
  Badge,
  Table,
  Space,
  Card,
  Button,
  Typography,
  Empty,
  Flex,
} from 'antd';
import {
  FileTextOutlined,
  GlobalOutlined,
  SyncOutlined,
  PlusCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import TemplateCreateEdit from './CreateEdit';
import { MenuOptions, PagePanel } from '../../../components';
import { ContainerTemplate } from './styles';

const { Text } = Typography;

const WhatsappTemplates: React.FC = () => {
  const { addToast } = useToast();
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/whatsapp/templates');
      // Ajuste dependendo de como seu backend retorna (geralmente data.data na Meta)
      setTemplates(response.data?.data || response.data || []);
    } catch (err) {
      const status = (err as any)?.response?.status;
      const isUnauthorized = status === 401;
      const errorMessage =
        (err as any)?.response?.data?.error ||
        (err as any)?.response?.data?.message ||
        (err as any)?.message ||
        '';

      const isSilentExpectedError =
        !isUnauthorized &&
        (/não cadastrada/i.test(errorMessage) ||
          /não encontrada/i.test(errorMessage) ||
          /configuração/i.test(errorMessage));

      if (!isSilentExpectedError) {
        addToast({
          type: 'error',
          title: 'Erro',
          description:
            errorMessage?.toString().slice(0, 160) ||
            'Erro ao carregar templates da Cloud API.',
        });
      }
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleEdit = (template: any) => {
    setSelectedTemplate(template);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedTemplate(null);
    setModalOpen(true);
  };

  const getStatusColor = (status: string) => {
    const s = status?.toUpperCase();
    if (s === 'APPROVED') return 'success';
    if (s === 'PENDING') return 'processing';
    if (s === 'REJECTED') return 'error';
    return 'default';
  };

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (val: string) => (
        <Text strong className="text-blue-600">
          {val}
        </Text>
      ),
    },
    {
      title: 'Categoria',
      dataIndex: 'category',
      key: 'category',
      render: (val: string) => {
        let color = 'blue';
        if (val === 'MARKETING') color = 'orange';
        if (val === 'AUTHENTICATION') color = 'purple';
        return <Tag color={color}>{val}</Tag>;
      },
    },
    {
      title: 'Idioma',
      dataIndex: 'language',
      key: 'language',
      render: (val: string) => (
        <Space>
          <GlobalOutlined /> {val}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Badge status={getStatusColor(status) as any} text={status} />
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 100,
      render: (record: any) => (
        <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>
          Editar
        </Button>
      ),
    },
  ];

  return (
    <ContainerTemplate>
      <PagePanel>
        <Flex justify="space-between" align="center" gap={16}>
          <Text strong style={{ fontSize: 20 }}>
            Templates de Mensagem (Meta Cloud API)
          </Text>
          <MenuOptions
            menu_items={[
              {
                label: 'Sincronizar com Meta',
                key: 'sync',
                icon: <SyncOutlined spin={loading} />,
                onClick: loadTemplates,
              },
              {
                label: 'Novo Template',
                key: 'new',
                icon: <PlusCircleOutlined />,
                onClick: handleCreate,
              },
            ]}
          />
        </Flex>
        <Divider />

        <Table
          loading={loading}
          dataSource={templates}
          rowKey="id"
          columns={columns}
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: <Empty description="Nenhum template encontrado" />,
          }}
          expandable={{
            expandedRowRender: (record) => {
              const body = record.components?.find(
                (c: any) => c.type === 'BODY',
              );
              return (
                <Card
                  size="small"
                  style={{
                    background: '#f9f9f9',
                    borderLeft: '3px solid #1890ff',
                  }}
                >
                  <Text
                    type="secondary"
                    style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}
                  >
                    {body?.text || 'Sem conteúdo de texto.'}
                  </Text>
                </Card>
              );
            },
          }}
        />
      </PagePanel>
      {modalOpen && (
        <TemplateCreateEdit
          open={modalOpen}
          template={selectedTemplate}
          close={() => setModalOpen(false)}
          onSuccess={loadTemplates} // Recarrega a tabela ao salvar
        />
      )}
    </ContainerTemplate>
  );
};

export default WhatsappTemplates;
